// Where a sign-in may send someone on: a path on THIS site, and nothing else.
//
// `next` rides in a query string, which is the visitor's to edit, and it ends up
// in `location.replace(next)` / `href`. The three copies of this check that
// lived in /login, /auth/land and lib/onboarding/arrival only asked "starts
// with a slash, not two", which passes shapes a URL parser reads very
// differently from a person:
//
//   /\evil.example        a browser treats "\" as "/" in a web url → //evil.example
//   /<tab>/evil.example   the parser DELETES tab, CR and LF anywhere → //evil.example
//
// Both are "relative" to a string check and cross-origin to a navigation. So the
// rule here is the strict one, and it is the same everywhere:
//
//   · begins with exactly one "/" (never "//", never "/\")
//   · no backslash anywhere, no control character anywhere (tab, CR, LF, NUL…)
//   · and the URL parser, asked to resolve it against this site, stays on this site
//
// Pure: no React, no I/O, no window. The pages and the arrival rules read it from
// here, and the test pins the hostile cases.

/** The longest `next` we will carry: a consent url with every OAuth parameter fits in well under this. */
const MAX_LENGTH = 2000

/** Any ASCII control character (0x00–0x1f, 0x7f) — the ones a URL parser strips or a header splits on. */
// eslint-disable-next-line no-control-regex
const CONTROL = /[\u0000-\u001f\u007f]/

/** A stand-in origin to resolve against: only ever compared with itself, never used as a destination. */
const BASE = 'https://innernet.invalid'

export function safeNext(raw: string | null | undefined, fallback = '/dashboard'): string {
  if (typeof raw !== 'string') return fallback
  if (raw.length === 0 || raw.length > MAX_LENGTH) return fallback
  if (raw[0] !== '/') return fallback
  if (raw[1] === '/' || raw[1] === '\\') return fallback
  if (raw.includes('\\') || CONTROL.test(raw)) return fallback
  try {
    // whatever the parser makes of it, it must still be this site
    if (new URL(raw, BASE).origin !== BASE) return fallback
  } catch {
    return fallback
  }
  return raw
}
